import { useCallback } from "react";

import { Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useCurrenciesQuery } from "@sso/apis/currency";
import {
  useAssignTrialLicenceMutation,
  usePurchaseSoftwareLicenceMutation,
  useRedeemPendingLicenceQuery,
  useUpdatePendingLicenceCurrencyMutation,
} from "@sso/apis/pendingLicencePurchase";
import CurrencySelect from "@sso/components/CurrencySelect";
import { LicenceType, PaymentType, PurchaseLicenceRequest, SoftwareLicence, SoftwareLicenceSummary } from "@sso/types";
import { ColumnContainer, RowContainer } from "@sso/ui/FlexBox";
import { Typography } from "@sso/ui/Typography";

import LicenceCard from "./LicenceCard";

const LicenceRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 1rem;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  height: 90%;
  justify-content: center;
  gap: 1rem;
`;

const CurrencyContainer = styled(ColumnContainer)`
  .select-with-label-group {
    margin-bottom: 0 !important;
  }
`;

const StyledColumnContainer = styled(ColumnContainer)`
  overflow: auto;
`;

const StyledLink = styled.a`
  color: blue;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const LoadingContainer = styled(ColumnContainer)`
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

export const PurchaseLicence = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const id = searchParams.get("id")!;
  const hash = searchParams.get("hash")!;
  const licenceName = undefined;

  const navigate = useNavigate();

  const { data, error, isLoading } = useRedeemPendingLicenceQuery({
    pendingLicencePurchaseId: id,
    hash,
  });

  const { data: currencies, error: currenciesError, isLoading: currenciesLoading } = useCurrenciesQuery();

  const [AssignTrialLicence] = useAssignTrialLicenceMutation();
  const [PurchaseSoftwareLicence] = usePurchaseSoftwareLicenceMutation();

  const onSubscribe = useCallback(
    async (licence: SoftwareLicenceSummary) => {
      if (licence.licenceType === LicenceType.TRIAL) {
        try {
          const response = await AssignTrialLicence({
            pendingLicencePurchaseId: id,
            hash,
            softwareLicenceId: licence.softwareLicenceId,
          }).unwrap();

          const softwareUrl = data?.software.url;
          if (softwareUrl) {
           const url = `${softwareUrl}?token=${encodeURIComponent(response.token)}&refreshToken=${encodeURIComponent(response.refreshToken)}`;
           window.location.href = url;
          } else {
            navigate("/auth/signin");
          }
        } catch (error) {
          navigate("/auth/signin");
        }
      } else {
        const request: PurchaseLicenceRequest = {
          softwareLicenceId: licence.softwareLicenceId,
          pendingLicencePurchaseId: id,
          hash: hash,
        };
        try {
          const r = await PurchaseSoftwareLicence(request).unwrap();
          window.location.href = r;
        } catch (error) {
          navigate("/auth/signin");
        }
      }
    },
    [AssignTrialLicence, navigate, id, hash, data, PurchaseSoftwareLicence],
  );

  const filteredLicences = data?.software.softwareLicences
    .filter(licence => licenceName === undefined || (licence.name !== licenceName && licence.name !== "Starter"))
    .filter(x => x.licenceType === LicenceType.SINGLE_USER || x.licenceType === LicenceType.TRIAL || x.licenceType === LicenceType.CONCURRENT)
    .filter(l => l.paymentType === PaymentType.RecurringPayment || l.paymentType === PaymentType.Free)
    .sort((a, b) => {
      const aRules = a.pricingRules;
      const bRules = b.pricingRules;

      const aHasRules = Array.isArray(aRules) && aRules.length > 0;
      const bHasRules = Array.isArray(bRules) && bRules.length > 0;

      if (!aHasRules && !bHasRules) {
        return 0;
      }
      if (!aHasRules) {
        return -1;
      }
      if (!bHasRules) {
        return 1;
      }

      return aRules[0].transactionCostIncTax! - bRules[0].transactionCostIncTax!;
    });

  const [updatePendingLicenceCurrency, { isLoading: isUpdatingCurrency }] = useUpdatePendingLicenceCurrencyMutation();

  const changePaymentCurrency = useCallback(
    async (currencyCode: string) => {
      try {
        await updatePendingLicenceCurrency({
          pendingLicencePurchaseId: id,
          hash,
          transactionCurrencyCode: currencyCode,
        }).unwrap();
      } catch (err) {}
    },
    [updatePendingLicenceCurrency, id, hash],
  );

  if (error || (!isLoading && !data && (!filteredLicences || filteredLicences.length === 0))) {
    return (
      <ColumnContainer
        align="flex-start"
        justify="flex-start"
        flexGrow={1}
        overflow="hidden"
        position="relative"
        padding="5%"
      >
        <Typography fontWeight={500}>Error</Typography>
        <Typography fontWeight={100}>Error loading licences</Typography>
      </ColumnContainer>
    );
  }

  if (isLoading || currenciesLoading) {
    return (
      <LoadingContainer>
        <RowContainer gap="1rem" justify="center" align="center" width="100%">
          <Spinner animation="border" role="status" />
          <Typography fontWeight={500} size="1.5rem">
            Loading Licences
          </Typography>
        </RowContainer>
      </LoadingContainer>
    );
  }

  return (
    <StyledColumnContainer
      align="flex-start"
      justify="flex-start"
      flexGrow={1}
      overflow="hidden"
      position="relative"
      padding="2% 5% 5% 5%"
      gap=".5rem"
      height="100%"
    >
      <Typography fontWeight={500} size={"1.5rem"}>
        {data?.software.name} Plans
      </Typography>
      <Typography fontWeight={100} size={"1.2rem"}>
        There are {filteredLicences?.length ?? 0} available plans for you to choose from. Please select the one that
        best suits your needs.
      </Typography>
      <RowContainer gap=".5rem" margin="1rem 0 0 0" justify="flex-start" width="100%">
        <Typography fontWeight={100} size={"1.2rem"}>
          If you don't see the plan you want, please{" "}
        </Typography>
        <Typography fontWeight={100} size={"1.2rem"}>
          <StyledLink href="mailto:support@basemap.co.uk"> contact us</StyledLink>
        </Typography>
      </RowContainer>

      <RowContainer gap=".5rem" margin="1rem 0 0 0" justify="flex-start" width="100%">
        <ColumnContainer align="flex-start" maxWidth="25%">
          <Typography fontWeight={100} size={"1.2rem"}>
            Displaying prices in:
          </Typography>
        </ColumnContainer>
        <CurrencyContainer margin="0 0 0 0" align="flex-end" width="25%">
          <CurrencySelect
            onCurrencyChange={changePaymentCurrency}
            currencyCode={data?.transactionCurrency?.code ?? "GBP"}
            currencies={currencies ?? []}
          />
        </CurrencyContainer>
      </RowContainer>

      <LicenceRow>
        {isLoading && <Typography>Loading...</Typography>}
        {data &&
          filteredLicences?.map(licence => (
            <LicenceCard
              loading={isUpdatingCurrency}
              onSubscribe={onSubscribe}
              key={licence.softwareLicenceId}
              licence={licence}
              transactionCurrency={data.transactionCurrency}
            />
          ))}
      </LicenceRow>
    </StyledColumnContainer>
  );
};
